import React from "react";
import UseContext from "hooks/useContext";
import SectionWrapper from "pages/createMission/components/SectionWrapper";
import Switch from "components/switch/Switch";
import StyledInput from "components/styledInput/StyledInput";
import {
    normalizePenibilite,
    useRiskyPositions,
} from "pages/missionTemplates/utils/templateRiskyPositions";

const ARTICLE_L4161_1_URL =
    "https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000035640694";
const POSITION_AT_RISK_SWITCH_ID = "mission-template-position-at-risk";

const RiskyPositionTemplate = () => {
    const { templateData, setTemplateData, language } = UseContext();
    const { riskyPositions, isLoading, hasError } = useRiskyPositions();

    const isPositionAtRisk = !!templateData.position_at_risk;
    const selectedPenibilite = normalizePenibilite(templateData.penibilite);

    const handlePositionAtRiskChange = (checked) =>
        setTemplateData((prev) => ({ ...prev, position_at_risk: checked }));

    const togglePenibilite = (besttField) =>
        setTemplateData((prev) => {
            const nextPenibilite = normalizePenibilite(prev.penibilite);
            if (nextPenibilite[besttField]) delete nextPenibilite[besttField];
            else nextPenibilite[besttField] = true;
            return { ...prev, penibilite: nextPenibilite };
        });

    const renderRiskyPositionList = () => {
        if (isLoading) {
            return <div className="h-32 bg-gray-100 rounded-lg animate-pulse" />;
        }
        if (hasError) {
            return (
                <p className="ft-sm text-red-500">
                    {language.getValue(
                        "missionTemplate.riskyPosition.loadError",
                        "Impossible de charger la liste des postes à risques. Veuillez réessayer plus tard."
                    )}
                </p>
            );
        }
        if (riskyPositions.length === 0) {
            return (
                <p className="ft-sm text-gray-500">
                    {language.getValue(
                        "missionTemplate.riskyPosition.empty",
                        "Aucun poste à risque disponible"
                    )}
                </p>
            );
        }
        return (
            <fieldset className="min-w-0 bg-white border border-gray-100 rounded-lg overflow-hidden">
                <legend className="sr-only">
                    {language.getValue(
                        "missionTemplate.riskyPosition.listLabel",
                        "Postes à risques concernés"
                    )}
                </legend>
                <ul className="divide-y divide-gray-100">
                    {riskyPositions.map(({ besttField, name }) => (
                        <li
                            key={besttField}
                            className="flex px-5 py-4 hover:bg-gray-50"
                        >
                            <StyledInput
                                type={"checkbox"}
                                checked={!!selectedPenibilite[besttField]}
                                onChange={() => togglePenibilite(besttField)}
                                label={
                                    <span className="min-w-0 break-words">{name}</span>
                                }
                            />
                        </li>
                    ))}
                </ul>
            </fieldset>
        );
    };

    return (
        <SectionWrapper
            title={language.getValue(
                "missionTemplate.riskyPosition.title",
                "Poste à risque"
            )}
            description={language.getValue(
                "missionTemplate.riskyPosition.description",
                "Certains postes exigent une formation renforcée à la sécurité."
            )}
        >
            <div className="flex items-center justify-between gap-4 px-6 py-4 bg-white border border-gray-100 rounded-lg">
                <div className="flex flex-col gap-1 min-w-0">
                    <label
                        htmlFor={POSITION_AT_RISK_SWITCH_ID}
                        className="ft-sm font-medium text-gray-900 cursor-pointer"
                    >
                        {language.getValue(
                            "missionTemplate.riskyPosition.question",
                            "Ce poste est-il à risque ?"
                        )}
                    </label>
                    <a
                        href={ARTICLE_L4161_1_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-fit ft-sm text-gray-500 hover:underline"
                    >
                        {language.getValue(
                            "missionTemplate.riskyPosition.articleLink",
                            "Voir l'article L. 4161-1 du Code du travail"
                        )}
                    </a>
                </div>
                <Switch
                    id={POSITION_AT_RISK_SWITCH_ID}
                    checked={isPositionAtRisk}
                    onChange={handlePositionAtRiskChange}
                />
            </div>
            {isPositionAtRisk && renderRiskyPositionList()}
        </SectionWrapper>
    );
};

export default RiskyPositionTemplate;
